import { Box, Modal } from "@mui/material";
import { IoClose } from "react-icons/io5";
import { LuDownload } from "react-icons/lu";
import moment from "moment";
import LogoOlin from "../../assets/logoOlin.png";
import jsPDF from "jspdf";

const RECEIPT_WIDTH = 380;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "transparent",
  boxShadow: "none",
  outline: "none",
  border: "none",
  width: "fit-content",
};

const loadImageWithSize = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({
        data: canvas.toDataURL("image/png"),
        width: img.naturalWidth,
        height: img.naturalHeight,
        ratio: img.naturalWidth / img.naturalHeight,
      });
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

const PrescriptionModal = ({ open, setOpen, data }) => {
  const handleClose = () => setOpen(false);

  moment.locale("id");

  const scale = Math.min(1, (window.innerWidth * 0.95) / RECEIPT_WIDTH);
  const scaleHeightOffset = (1 - scale) * -100;

  const handleDownloadPDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a5",
    });

    const fontName = "helvetica";

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentW = pageW - margin * 2;
    let y = margin;

    const colorSlate700 = [51, 65, 85];
    const colorSlate500 = [100, 116, 139];
    const colorSlate400 = [148, 163, 184];

    const setF = (style = "normal", size = 8) => {
      doc.setFont(fontName, style);
      doc.setFontSize(size);
    };

    setF("bold", 11);
    doc.setTextColor(...colorSlate700);
    doc.text(data?.doctor_name || "-", margin, y);
    y += 5;

    const logoImg = await loadImageWithSize(LogoOlin);
    const logoH = 9;
    const logoW = logoImg ? Math.min(logoImg.ratio * logoH, 30) : 0;
    const logoX = pageW - margin - logoW;

    setF("normal", 5.5);
    doc.setTextColor(...colorSlate500);
    doc.text("Powered by", logoX + logoW / 2, y, { align: "center" });
    y += 3;

    if (logoImg) {
      doc.addImage(
        logoImg.data,
        "PNG",
        logoX,
        y,
        logoW,
        logoH,
        undefined,
        "FAST",
      );
    }

    const labelX = margin;
    const colonX = margin + 12;
    const valueX = margin + 14;
    const maxValueW = logoX - valueX - 3;

    let infoY = y;
    setF("normal", 7);
    doc.setTextColor(...colorSlate700);

    doc.text("No. SIP", labelX, infoY);
    doc.text(":", colonX, infoY);
    const sipLines = doc.splitTextToSize(data?.doctor_sip || "-", maxValueW);
    doc.text(sipLines, valueX, infoY);
    infoY += sipLines.length * 3.8 + 0.5;

    doc.text("Alamat", labelX, infoY);
    doc.text(":", colonX, infoY);
    const alamatLines = doc.splitTextToSize(
      data?.doctor_address || "-",
      maxValueW,
    );
    doc.text(alamatLines, valueX, infoY);

    y = Math.max(y + logoH, infoY + alamatLines.length * 3.8) + 5;

    doc.setDrawColor(...colorSlate700);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageW - margin, y);
    y += 4;

    setF("normal", 7);
    doc.setTextColor(...colorSlate500);
    doc.text(data?.recipe_date || "", pageW - margin, y, { align: "right" });
    y += 7;

    setF("bold", 11);
    doc.setTextColor(...colorSlate700);
    doc.text("RESEP DIGITAL", pageW / 2, y, { align: "center" });
    y += 5;

    setF("normal", 7);
    doc.setTextColor(...colorSlate500);
    doc.text(`No. Resep: ${data?.recipe_number || "-"}`, pageW / 2, y, {
      align: "center",
    });
    y += 8;

    if (data?.recipes?.length > 0) {
      data.recipes.forEach((drug) => {
        setF("bold", 8);
        doc.setTextColor(...colorSlate700);
        doc.text("R/", margin, y);

        const nameLines = doc.splitTextToSize(
          drug?.product_name || "-",
          contentW - 22,
        );
        doc.text(nameLines, margin + 6, y);

        doc.text(`${drug.quantity} ${drug.uom}`, pageW - margin, y, {
          align: "right",
        });
        y += nameLines.length * 4;

        if (drug?.notes) {
          setF("normal", 7);
          doc.setTextColor(...colorSlate500);
          const noteLines = doc.splitTextToSize(drug.notes, contentW - 8);
          doc.text(noteLines, margin + 6, y);
          y += noteLines.length * 3.5;
        }

        doc.setDrawColor(...colorSlate700);
        doc.setLineWidth(0.2);
        doc.line(margin + 5, y + 1, pageW - margin, y + 1);
        y += 6;
      });
    }

    y = Math.max(y + 4, pageH - 22);

    setF("normal", 7);
    doc.setTextColor(...colorSlate700);
    doc.text("Pro", margin, y);
    doc.text(":", margin + 10, y);
    doc.text(data?.patient_name || "-", margin + 13, y);
    y += 4;
    doc.text("Umur", margin, y);
    doc.text(":", margin + 10, y);
    doc.text(`${data?.patient_age || "-"} Tahun`, margin + 13, y);
    y += 6;

    setF("italic", 6);
    doc.setTextColor(...colorSlate400);
    doc.text(
      "* Resep digital hanya berlaku untuk ditebus di ekosistem OLIN",
      pageW - margin,
      y,
      { align: "right" },
    );

    const filename = `RESEP_${data?.recipe_number || "resep"}_${data?.patient_name || "pasien"}.pdf`;
    doc.save(filename);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...style, padding: 0 }} className="text-left z-[9999]">
        <div
          style={{
            width: RECEIPT_WIDTH,
            transformOrigin: "top center",
            transform: `scale(${scale})`,
            marginBottom: `${scaleHeightOffset}%`,
          }}
        >
          <div className="p-3 bg-slate-100 rounded-xl">
            <div className="flex justify-between items-center mb-4">
              <p className="text-lg font-semibold text-slate-700">e-Resep</p>
              <IoClose
                className="hover:text-red-700 cursor-pointer w-5 md:w-6 h-full hover:bg-gray-100 rounded-md transition-transform duration-300 ease-in-out hover:rotate-90"
                onClick={handleClose}
              />
            </div>

            <div className="bg-white border border-gray-300 p-3 flex flex-col min-h-[480px] h-full">
              <div className="flex-grow">
                <div className="flex items-center justify-between">
                  <div className="w-full">
                    <p className="font-semibold text-[13px] mb-1 text-slate-700">
                      {data && data.doctor_name}
                    </p>
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex text-[9px] text-slate-700">
                        <div>
                          <p>No. SIP</p>
                          <p>Alamat</p>
                        </div>
                        <div className="ml-3 mr-1">
                          <p>:</p>
                          <p>:</p>
                        </div>
                        <div>
                          <p className="break-words">
                            {data && data.doctor_sip}
                          </p>
                          <p className="break-words">
                            {data && data.doctor_address}
                          </p>
                        </div>
                      </div>
                      <div className="w-1/4 flex flex-col items-center justify-center gap-1">
                        <p className="text-[9px] text-slate-500">Powered by</p>
                        <img
                          src={LogoOlin}
                          alt="Olin Logo"
                          className="w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border border-slate-700 my-2"></div>
                <div className="w-full flex justify-end">
                  <p className="text-[9px] text-slate-500">
                    {data && data.recipe_date}
                  </p>
                </div>
                <div className="w-full flex flex-col items-center justify-center my-4">
                  <h1 className="text-slate-700 font-semibold text-base">
                    RESEP DIGITAL
                  </h1>
                  <p className="text-[9px] text-slate-500">
                    No. Resep: {data && data.recipe_number}
                  </p>
                </div>
                <div className="my-3">
                  {data?.recipes?.map((drug, index) => (
                    <div
                      className="w-full text-[9px] text-slate-700 mt-2"
                      key={index}
                    >
                      <div className="flex w-full">
                        <p className="mr-2 text-[10px] font-medium">R/</p>
                        <div className="flex justify-between items-center w-full">
                          <div className="w-full">
                            <div className="w-full flex items-start justify-between gap-2">
                              <p className="text-[10px] font-medium">
                                {drug?.product_name === ""
                                  ? "-"
                                  : drug?.product_name}
                              </p>
                              <p className="w-fit text-[10px] font-medium">
                                {drug.quantity} {drug.uom}
                              </p>
                            </div>
                            <p>{drug?.notes}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end ml-5 mt-2 items-center w-full border-b border-slate-700"></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex text-[9px] text-slate-700 mt-4">
                <div>
                  <p>Pro</p>
                  <p>Umur</p>
                </div>
                <div className="ml-3 mr-1">
                  <p>:</p>
                  <p>:</p>
                </div>
                <div>
                  <p>{data && data.patient_name}</p>
                  <p>{data && data.patient_age} Tahun</p>
                </div>
              </div>

              <div className="flex w-full items-center justify-end mt-4">
                <p className="text-slate-400 text-[8px]">
                  * Resep digital hanya berlaku untuk ditebus di ekosistem OLIN
                </p>
              </div>
            </div>

            <div className="w-full flex items-center justify-end gap-2 mt-3 font-medium text-md">
              <button
                className="py-1.5 text-slate-500 border border-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200 cursor-pointer px-4 transition-all duration-300"
                onClick={handleClose}
                type="button"
              >
                Tutup
              </button>
              <button
                className="py-1.5 text-white border border-secondary bg-secondary rounded-lg hover:bg-secondaryHover cursor-pointer px-4 transition-all duration-300 flex items-center gap-2"
                onClick={handleDownloadPDF}
                type="button"
              >
                <LuDownload size={15} />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default PrescriptionModal;
